import { Folder, FolderPlus, GripVertical, Inbox, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { ArchiveFolder } from "@/lib/composerArchive";
import { ALL, UNFILED } from "./useArchivePanel";

type Props = {
  folders: ArchiveFolder[];
  counts: { byFolder: Map<string, number>; unfiled: number; total: number };
  activeFolder: string;
  setActiveFolder: (id: string) => void;
  newFolderName: string;
  setNewFolderName: (v: string) => void;
  createPending: boolean;
  onCreate: (name: string) => void;
  dragFolderId: string | null;
  setDragFolderId: (id: string | null) => void;
  onDropOnFolder: (folderId: string | null) => void;
  onFolderReorderDrop: (targetId: string) => void;
  onRequestRename: (f: ArchiveFolder) => void;
  onRequestDelete: (f: ArchiveFolder) => void;
};

export function ArchiveFolderRail({
  folders,
  counts,
  activeFolder,
  setActiveFolder,
  newFolderName,
  setNewFolderName,
  createPending,
  onCreate,
  dragFolderId,
  setDragFolderId,
  onDropOnFolder,
  onFolderReorderDrop,
  onRequestRename,
  onRequestDelete,
}: Props) {
  return (
    <div className="flex w-48 shrink-0 flex-col border-r border-border/60">
      <div className="flex items-center gap-1 p-2">
        <Input
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          placeholder="New folder…"
          className="h-7 text-xs"
          onKeyDown={(e) => {
            if (e.key === "Enter" && newFolderName.trim()) onCreate(newFolderName);
          }}
        />
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7 shrink-0"
          disabled={!newFolderName.trim() || createPending}
          onClick={() => onCreate(newFolderName)}
        >
          <FolderPlus className="h-3.5 w-3.5" />
        </Button>
      </div>
      <ScrollArea className="flex-1">
        <div className="space-y-0.5 p-1">
          <button
            type="button"
            onClick={() => setActiveFolder(ALL)}
            className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors ${
              activeFolder === ALL ? "bg-accent text-accent-foreground" : "hover:bg-muted/50"
            }`}
          >
            <span className="flex items-center gap-1.5 truncate">
              <Folder className="h-3.5 w-3.5" /> All pieces
            </span>
            <Badge variant="secondary" className="h-4 px-1 text-[10px]">{counts.total}</Badge>
          </button>
          <button
            type="button"
            onClick={() => setActiveFolder(UNFILED)}
            onDragOver={(e) => {
              e.preventDefault();
            }}
            onDrop={() => onDropOnFolder(null)}
            className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors ${
              activeFolder === UNFILED ? "bg-accent text-accent-foreground" : "hover:bg-muted/50"
            }`}
          >
            <span className="flex items-center gap-1.5 truncate">
              <Inbox className="h-3.5 w-3.5" /> Unfiled
            </span>
            <Badge variant="secondary" className="h-4 px-1 text-[10px]">{counts.unfiled}</Badge>
          </button>
          {folders.map((f) => (
            <div
              key={f.id}
              draggable
              onDragStart={(e) => {
                setDragFolderId(f.id);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragEnd={() => setDragFolderId(null)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragFolderId) onFolderReorderDrop(f.id);
                else onDropOnFolder(f.id);
              }}
              className={`group flex items-center justify-between gap-1 rounded px-2 py-1.5 text-xs transition-colors ${
                activeFolder === f.id ? "bg-accent text-accent-foreground" : "hover:bg-muted/50"
              } ${dragFolderId === f.id ? "opacity-50" : ""}`}
            >
              <GripVertical className="h-3 w-3 shrink-0 cursor-grab text-muted-foreground/60" />
              <button
                type="button"
                onClick={() => setActiveFolder(f.id)}
                className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
              >
                <Folder className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{f.name}</span>
              </button>
              <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                {counts.byFolder.get(f.id) ?? 0}
              </Badge>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-5 w-5 opacity-0 group-hover:opacity-100"
                  >
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onRequestRename(f)}>
                    <Pencil className="mr-2 h-3.5 w-3.5" /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => onRequestDelete(f)}
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
